import { Link } from "react-router-dom";
import { CheckCircle2, Award, BookOpen, Music2, Mic2, Drama, Globe2, GraduationCap, MessageCircle } from "lucide-react";
import PageHero from "../components/PageHero";
import { SCHOOL } from "../lib/site";

const DEPARTMENTS = [
  {
    icon: Music2,
    title: "Strumenti Classici",
    desc: "Archi e Arpa · Ottoni · Legni · Pianoforte · Tastiera elettronica e Organo · Chitarra · Strumenti a Percussione.",
  },
  {
    icon: Award,
    title: "Pop · Rock · Jazz",
    desc: "Esami Trinity Rock & Pop e Jazz: chitarra, basso, batteria, tastiera, voce, ukulele e Legni Jazz.",
  },
  {
    icon: Mic2,
    title: "Canto",
    desc: "Canto Lirico (Classical) e Canto Moderno (Rock & Pop Vocals). Repertorio personalizzabile e flessibile.",
  },
  {
    icon: BookOpen,
    title: "Theory of Music",
    desc: "Esami scritti di teoria musicale dal Grade 1 al Grade 8. Prerequisito per i grade strumentali avanzati.",
  },
  {
    icon: Drama,
    title: "Drama · Performing Arts",
    desc: "Certificazioni in Drama e Arti Performative, disponibili in lingua inglese, anche in modalità digitale.",
  },
  {
    icon: GraduationCap,
    title: "Esami in forma di Concerto",
    desc: "Performance certificate in forma di Recital per solisti, ensemble (duo, trio…) e gruppi più numerosi, in tre livelli.",
  },
];

const GRADES = [
  { label: "Initial", desc: "Livello propedeutico, primo accesso ai grade Trinity." },
  { label: "Grade 1 — 2", desc: "Fondamenti tecnici e primo repertorio." },
  { label: "Grade 3 — 5", desc: "Livello intermedio, lettura e interpretazione." },
  { label: "Grade 6 — 7", desc: "Livello avanzato, repertorio articolato." },
  { label: "Grade 8", desc: "Livello pre-professionale, accesso a percorsi accademici." },
];

const BENEFITS = [
  "Riconoscimento internazionale in oltre 60 paesi",
  "Validi come credito formativo all'Esame di Stato (D.M. Istruzione e Merito)",
  "Inseribili nel Curriculum dello Studente",
  "Utilizzabili per ammissione a corsi accademici, stage e tirocini",
  "Repertorio flessibile e personalizzabile",
  "Esaminatori inviati direttamente da Londra",
];

export default function Certificazioni() {
  const waLink = `https://api.whatsapp.com/send?phone=${SCHOOL.whatsapp.number.replace("+", "")}&text=${encodeURIComponent(SCHOOL.whatsapp.text)}`;

  return (
    <div data-testid="page-certificazioni">
      <PageHero
        eyebrow={`Trinity College London · Centre Registration ${SCHOOL.trinityCentreId}`}
        title="Corsi e certificazioni"
        italic="Trinity College London."
        subtitle="I corsi Trinity sono corsi finalizzati al conseguimento delle certificazioni delle competenze rilasciate dal Trinity College London — ente certificatore internazionale, attivo dal 1877 e presente in oltre 60 paesi nel mondo."
      />

      {/* Intro */}
      <section className="py-24 px-6 md:px-12 lg:px-24 max-w-[1400px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-20">
          <div className="lg:col-span-5">
            <div className="text-[11px] tracking-eyebrow text-[#A3133A] mb-4">Standard internazionale</div>
            <h2 className="font-serif-display text-4xl sm:text-5xl text-[#1C1C1C] leading-tight">
              Una qualifica<br />riconosciuta nel mondo.
            </h2>
          </div>
          <div className="lg:col-span-7 text-[#5C5C5C] leading-relaxed space-y-5 text-base sm:text-lg">
            <p>
              Le qualifiche musicali Trinity sono riconosciute in <strong className="text-[#1C1C1C]">oltre 60 paesi</strong>
              {" "}e rappresentano uno standard di riferimento per studenti, insegnanti e istituzioni educative.
            </p>
            <p>
              I nostri corsi riguardano tutti gli strumenti dei vari dipartimenti attivi —
              <strong className="text-[#1C1C1C]"> classici, pop / rock, jazz</strong> — nonché canto lirico
              e moderno e il dipartimento <strong className="text-[#1C1C1C]">Drama (Teatro Musicale)</strong>.
            </p>
            <p>
              Le certificazioni possono essere utilizzate per l'ammissione ai corsi accademici o per stage e
              tirocini di indirizzo, non solo in Italia ma anche in oltre 60 paesi in cui opera Trinity.
            </p>
          </div>
        </div>

        {/* Dipartimenti */}
        <div className="text-[11px] tracking-eyebrow text-[#A3133A] mb-4">I dipartimenti</div>
        <h2 className="font-serif-display text-4xl sm:text-5xl text-[#1C1C1C] leading-tight mb-10">
          Sei aree<br />di certificazione.
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#A3133A]/15 border border-[#A3133A]/15 mb-20">
          {DEPARTMENTS.map((a, i) => (
            <div key={a.title} className="bg-[#FBFBF9] p-7" data-testid={`exam-area-${i}`}>
              <a.icon size={24} className="text-[#A3133A] mb-4" />
              <h3 className="font-serif-display text-2xl text-[#1C1C1C] mb-3 leading-tight">{a.title}</h3>
              <p className="text-sm text-[#5C5C5C] leading-relaxed">{a.desc}</p>
            </div>
          ))}
        </div>

        {/* Grade structure */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-20">
          <div className="lg:col-span-5">
            <div className="text-[11px] tracking-eyebrow text-[#A3133A] mb-4">La struttura</div>
            <h2 className="font-serif-display text-4xl sm:text-5xl text-[#1C1C1C] leading-tight mb-6">
              Nove livelli,<br />
              <em className="italic font-light">un percorso graduale.</em>
            </h2>
            <p className="text-[#5C5C5C] leading-relaxed mb-6">
              Gli esami strumentali Trinity sono graduati e strutturati in <strong className="text-[#1C1C1C]">nove livelli</strong>:
              dal propedeutico <em>Initial</em> al prestigioso <em>Grade 8</em>.
            </p>
            <p className="text-[#5C5C5C] leading-relaxed">
              Gli esami scritti di <strong className="text-[#1C1C1C]">Theory of Music</strong> sono disponibili
              in otto livelli (Grade 1 — 8). Gli esami in forma di Concerto o Recital sono articolati in
              tre livelli per solisti, ensemble e gruppi.
            </p>
          </div>
          <div className="lg:col-span-7">
            <div className="border border-[#A3133A]/15 divide-y divide-[#A3133A]/10">
              {GRADES.map((g, i) => (
                <div key={g.label} className="p-6 flex items-start gap-6 bg-white" data-testid={`grade-row-${i}`}>
                  <div className="font-serif-display text-3xl text-[#A3133A] w-32 sm:w-44 shrink-0">{g.label}</div>
                  <div className="text-[#5C5C5C] flex-1 pt-2">{g.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Drama e Arti Performative — banner separato */}
        <div className="bg-white border border-[#A3133A]/15 p-10 sm:p-14 grid grid-cols-1 md:grid-cols-12 gap-10 items-center mb-20">
          <div className="md:col-span-2 flex justify-center md:justify-start">
            <Drama size={56} className="text-[#A3133A]" />
          </div>
          <div className="md:col-span-10">
            <div className="text-[11px] tracking-eyebrow text-[#A3133A] mb-3">Dipartimento Drama</div>
            <h3 className="font-serif-display text-3xl sm:text-4xl text-[#1C1C1C] mb-4 leading-tight">
              Certificazioni in Drama e Arti Performative.
            </h3>
            <p className="text-[#5C5C5C] leading-relaxed">
              Anche queste certificazioni sono riconosciute come <strong className="text-[#1C1C1C]">crediti
              formativi all'Esame di Stato</strong> e nel Curriculum dello Studente introdotto dal Ministero
              dell'Istruzione. Sono disponibili in lingua inglese e possono essere sostenute in modalità
              digitale, consentendo ai candidati di esprimere appieno la loro creatività.
            </p>
          </div>
        </div>

        {/* Benefici */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 bg-[#A3133A] text-[#FBFBF9] p-10 md:p-16 relative overflow-hidden grain rounded-sm">
          <div className="lg:col-span-5 relative z-10">
            <Globe2 size={28} className="mb-5 opacity-90" />
            <div className="text-[11px] tracking-eyebrow opacity-80 mb-4">I vantaggi</div>
            <h3 className="font-serif-display text-4xl sm:text-5xl leading-tight mb-6">
              Perché scegliere<br />Trinity.
            </h3>
            <p className="opacity-90 leading-relaxed">
              Una certificazione Trinity attesta ufficialmente il livello tecnico ed espressivo raggiunto.
              È il passaporto musicale riconosciuto dai conservatori e dalle università di tutto il mondo.
            </p>
          </div>
          <div className="lg:col-span-7 relative z-10">
            <ul className="space-y-4">
              {BENEFITS.map((b, i) => (
                <li key={b} className="flex items-start gap-3 text-base" data-testid={`benefit-${i}`}>
                  <CheckCircle2 size={18} className="text-[#FBFBF9] mt-1 shrink-0 opacity-90" />
                  <span className="opacity-95">{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Centre + CTA */}
        <div className="mt-20 text-center">
          <div className="font-serif-display text-7xl sm:text-8xl text-[#A3133A] mb-2">{SCHOOL.trinityCentreId}</div>
          <div className="text-[11px] tracking-eyebrow text-[#5C5C5C] mb-10">Centre Registration Number · Trinity College London</div>

          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/iscrizione"
              data-testid="trinity-cta-iscrizione"
              className="bg-[#A3133A] text-white px-8 py-4 text-[12px] tracking-eyebrow rounded-sm hover:bg-[#8B0F2F] transition inline-flex items-center gap-2"
            >
              Iscriviti ai corsi Trinity
            </Link>
            <a
              href={waLink}
              target="_blank"
              rel="noreferrer"
              data-testid="trinity-cta-whatsapp"
              className="border border-[#A3133A] text-[#A3133A] px-8 py-4 text-[12px] tracking-eyebrow rounded-sm hover:bg-[#A3133A] hover:text-white transition inline-flex items-center gap-2"
            >
              <MessageCircle size={14} /> Chiedi info su WhatsApp
            </a>
          </div>

          <div className="mt-8 text-sm text-[#5C5C5C]">
            Oppure chiama: {SCHOOL.phones.slice(0, 2).map((p) => p.value).join(" · ")} · {SCHOOL.phones.slice(2).map((p) => p.value).join(" · ")}
          </div>
        </div>
      </section>
    </div>
  );
}
